/**
 * MapComponent TypeScript Interfaces
 *
 * Type definitions for the Map component that visualizes
 * GPS sensor data as interactive heatmaps on MapLibre GL JS.
 *
 * Created: January 2026
 * Phase 1: Core Map Component Implementation
 */

import type { Component } from '@/types';
import type { ChartDataSource } from '@/types/chartDataSource';
import type { ViewState } from 'react-map-gl/maplibre';

// ============================================================================
// Sensor Data Types
// ============================================================================

/**
 * Individual sensor data point with GPS coordinates and moisture reading
 */
export interface SensorDataPoint {
  /** Unique identifier for the data point */
  id: string;
  /** Device/sensor identifier */
  deviceId: string;
  /** GPS latitude coordinate */
  latitude: number;
  /** GPS longitude coordinate */
  longitude: number;
  /** Soil moisture level (0-100 percentage) */
  moistureLevel: number;
  /** Optional soil temperature reading */
  temperature?: number;
  /** Optional sensor depth in centimeters */
  depth?: number;
  /** Optional field zone identifier */
  zone?: string;
  /** Timestamp of the reading */
  timestamp: Date | string;
  /** Optional battery level percentage */
  batteryLevel?: number;
}

/**
 * GeoJSON Feature for a sensor point
 */
export interface SensorFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    id: string;
    deviceId: string;
    moisture: number;
    temperature?: number;
    zone?: string;
    timestamp: string;
  };
}

/**
 * GeoJSON FeatureCollection for all sensors
 */
export interface SensorGeoJSON {
  type: 'FeatureCollection';
  features: SensorFeature[];
}

// ============================================================================
// Zone Data Types (Phase 3)
// ============================================================================

/**
 * Field zone polygon data
 */
export interface ZoneDataPoint {
  /** Unique zone identifier */
  id: string;
  /** Zone display name */
  name: string;
  /** Average moisture level of sensors in zone */
  averageMoisture?: number;
  /** Number of sensors in zone */
  sensorCount?: number;
  /** Polygon coordinates (GeoJSON format) */
  coordinates: number[][][];
  /** Zone color override (optional) */
  color?: string;
  /** Zone metadata */
  metadata?: Record<string, unknown>;
}

/**
 * GeoJSON Feature for a zone polygon
 */
export interface ZoneFeature {
  type: 'Feature';
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  properties: {
    id: string;
    name: string;
    averageMoisture?: number;
    sensorCount?: number;
    color?: string;
  };
}

/**
 * GeoJSON FeatureCollection for zones
 */
export interface ZoneGeoJSON {
  type: 'FeatureCollection';
  features: ZoneFeature[];
}

/**
 * Cluster data point from Supercluster
 */
export interface ClusterPoint {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    cluster?: boolean;
    cluster_id?: number;
    point_count?: number;
    point_count_abbreviated?: string | number;
    /** Average moisture of clustered points */
    avgMoisture?: number;
    /** Individual point properties (when not clustered) */
    id?: string;
    deviceId?: string;
    moisture?: number;
    temperature?: number;
    zone?: string;
    timestamp?: string;
  };
}

// ============================================================================
// Map Groups & Pins (Phase: Groups, Pins & Navigation)
// ============================================================================

/**
 * Type of group for organizing map elements
 */
export type MapGroupType = 'sensor-type' | 'area' | 'custom';

/**
 * Map group definition for organizing sensors and pins
 */
export interface MapGroup {
  /** Unique identifier for the group */
  id: string;
  /** Display name of the group */
  name: string;
  /** Type of group */
  type: MapGroupType;
  /** Color used for group visualization */
  color: string;
  /** Optional icon identifier */
  icon?: string;
  /** Whether the group is currently visible */
  visible: boolean;
  /** IDs of sensors belonging to this group */
  sensorIds?: string[];
  /** IDs of pins belonging to this group */
  pinIds?: string[];
  /** Polygon coordinates for area-type groups */
  areaCoordinates?: number[][][];
  /** Optional description */
  description?: string;
  /** ISO timestamp of creation */
  createdAt: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Type alias: Layers are the user-facing name for groups.
 * Internally the interface remains MapGroup; "MapLayer" is for UI/API parity.
 */
export type MapLayer = MapGroup;

/**
 * GeoJSON Feature for a group polygon
 */
export interface MapGroupFeature {
  type: 'Feature';
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  properties: {
    id: string;
    name: string;
    color: string;
    visible: boolean;
  };
}

/**
 * GeoJSON FeatureCollection for groups
 */
export interface MapGroupGeoJSON {
  type: 'FeatureCollection';
  features: MapGroupFeature[];
}

/**
 * Pin styling configuration
 */
export interface MapPinStyle {
  /** Pin color */
  color: string;
  /** Icon type */
  icon?: 'location' | 'camera' | 'sensor' | 'power' | 'home' | 'building' | 'warehouse' | 'water' | 'waves' | 'rain' | 'flask' | 'temperature' | 'wind' | 'sun' | 'wifi' | 'radio' | 'flame' | 'warning' | 'maintenance' | 'gauge' | 'info' | 'custom';
  /** URL for custom icon (when icon is 'custom') */
  customIconUrl?: string;
  /** Scale multiplier for pin size */
  scale: number;
  /** Optional text label */
  label?: string;
  /** Position of label relative to pin */
  labelPosition?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Navigation configuration for a specific pin (follows ChartNavigationMapping pattern)
 */
export interface PinNavigationMapping {
  /** Target page ID to navigate to */
  targetPageId: string;
  /** Display name of target page (for UI) */
  targetPageName?: string;
  /** Label shown on navigation action */
  navigationLabel?: string;
  /** Parameters to pass to target page */
  parameters?: Record<string, unknown>;
  /** Whether to pass pin data as navigation parameters */
  passPinDataAsParams?: boolean;
  /** Transition animation type */
  transition?: 'fade' | 'slide' | 'instant';
  /** Conditional navigation rules */
  conditions?: Array<{
    field: string;
    operator: 'equals' | 'contains' | 'greater' | 'less';
    value: unknown;
  }>;
  /** Cursor style when hovering over navigable pin */
  cursor?: 'pointer' | 'default';
  /** Whether to highlight pin on hover */
  highlightOnHover?: boolean;
}

/**
 * Data binding for displaying live values in pin tooltips
 */
export interface PinDataBinding {
  id: string;
  dataSource: ChartDataSource;
  /** Query template ID — enables the template execution path for accurate results */
  queryTemplateId?: string;
  valueField: string;
  displayLabel: string;
  valueFormatter?: string;
  prefix?: string;
  suffix?: string;
  unit?: string;
  decimalPlaces?: number;
  fontSize?: string;
  fontWeight?: string | number;
  color?: string;
}

/**
 * Map pin definition
 */
export interface MapPin {
  /** Unique identifier for the pin */
  id: string;
  /** Display name of the pin */
  name: string;
  /** GPS latitude coordinate */
  latitude: number;
  /** GPS longitude coordinate */
  longitude: number;
  /** Pin styling configuration */
  style: MapPinStyle;
  /** Optional group this pin belongs to */
  groupId?: string;
  /** Navigation configuration for this pin */
  navigation?: PinNavigationMapping;
  /** Optional description */
  description?: string;
  /** Whether the pin is currently visible */
  visible: boolean;
  /** ISO timestamp of creation */
  createdAt: string;
  /** ISO timestamp of last update */
  updatedAt: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
  /** Data bindings for live values displayed in tooltip */
  dataBindings?: PinDataBinding[];
}

/**
 * GeoJSON Feature for a map pin
 */
export interface MapPinFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    id: string;
    name: string;
    color: string;
    icon: string;
    scale: number;
    label?: string;
    hasNavigation: boolean;
    targetPageId?: string;
    selected: boolean;
    hovered: boolean;
    imageId: string;
  };
}

/**
 * GeoJSON FeatureCollection for pins
 */
export interface MapPinGeoJSON {
  type: 'FeatureCollection';
  features: MapPinFeature[];
}

/**
 * Global pin navigation configuration
 */
export interface PinNavigationConfig {
  /** Whether pin navigation is enabled globally */
  enabled: boolean;
  /** Default target page for pins without specific navigation */
  defaultTarget?: string;
  /** Global parameter mapping from pin data to navigation params */
  parameterMapping?: Record<string, string>;
}

// ============================================================================
// Map View Settings
// ============================================================================

/**
 * Map viewport configuration
 */
export interface MapViewSettings {
  /** Center coordinates [longitude, latitude] */
  center: [number, number];
  /** Zoom level (0-22) */
  zoom: number;
  /** Camera pitch in degrees (0-85) */
  pitch?: number;
  /** Camera bearing/rotation in degrees (0-360) */
  bearing?: number;
  /** Bounding box [[sw_lng, sw_lat], [ne_lng, ne_lat]] */
  bounds?: [[number, number], [number, number]];
}

// ============================================================================
// Layer Configuration
// ============================================================================

/**
 * Heatmap layer configuration
 */
export interface HeatmapLayerConfig {
  /** Whether heatmap layer is enabled */
  enabled: boolean;
  /** Color scheme preset */
  colorScheme: 'agricultural' | 'thermal' | 'ocean' | 'grayscale' | 'custom';
  /** Heatmap radius in pixels */
  radius: number;
  /** Heatmap intensity multiplier */
  intensity: number;
  /** Layer opacity (0-1) */
  opacity: number;
  /** Custom color stops for 'custom' colorScheme */
  customColors?: string[];
}

/**
 * Marker layer configuration
 */
export interface MarkerLayerConfig {
  /** Whether marker layer is enabled */
  enabled: boolean;
  /** Show labels on markers */
  showLabels: boolean;
  /** Marker size preset */
  size: 'small' | 'medium' | 'large';
  /** Color markers by moisture value */
  colorByValue: boolean;
}

/**
 * Cluster layer configuration
 */
export interface ClusterLayerConfig {
  /** Whether clustering is enabled */
  enabled: boolean;
  /** Cluster radius in pixels */
  radius: number;
  /** Maximum zoom to cluster at */
  maxZoom: number;
}

/**
 * Field zone overlay configuration
 */
export interface ZoneLayerConfig {
  /** Whether zone overlays are enabled */
  enabled: boolean;
  /** Zone fill opacity (0-1) */
  fillOpacity: number;
  /** Zone border stroke width */
  strokeWidth: number;
}

/**
 * Complete layer configuration object
 */
export interface MapLayerConfig {
  heatmap: HeatmapLayerConfig;
  markers: MarkerLayerConfig;
  clusters: ClusterLayerConfig;
  zones: ZoneLayerConfig;
}

// ============================================================================
// Map Controls & Legend Configuration (Phase 5)
// ============================================================================

/**
 * Control position options (all four corners)
 */
export type ControlPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/**
 * Navigation controls configuration
 */
export interface MapControlsConfig {
  /** Position of the controls on the map */
  position: ControlPosition;
  /** Show zoom in/out buttons */
  showZoom: boolean;
  /** Show compass/north indicator */
  showCompass: boolean;
  /** Show fullscreen toggle button */
  showFullscreen: boolean;
  /** Show "fit to data" button */
  showFitBounds: boolean;
  /** Show scale indicator */
  showScale: boolean;
  /** Use compact mode for small containers */
  compact: boolean;
}

/**
 * Legend component configuration
 */
export interface MapLegendConfig {
  /** Position of the legend on the map */
  position: ControlPosition;
  /** Whether the legend is expanded or collapsed */
  expanded: boolean;
  /** Show layer toggle checkboxes */
  showLayerToggles: boolean;
  /** Show sensor count in legend */
  showSensorCount: boolean;
  /** Show last updated timestamp */
  showLastUpdated: boolean;
  /** Show moisture gradient scale (default false) */
  showMoistureGradient: boolean;
}

/**
 * Default navigation controls configuration
 */
export const DEFAULT_CONTROLS_CONFIG: MapControlsConfig = {
  position: 'top-right',
  showZoom: true,
  showCompass: true,
  showFullscreen: true,
  showFitBounds: true,
  showScale: true,
  compact: false,
};

/**
 * Default legend configuration
 */
export const DEFAULT_LEGEND_CONFIG: MapLegendConfig = {
  position: 'bottom-left',
  expanded: true,
  showLayerToggles: true,
  showSensorCount: true,
  showLastUpdated: true,
  showMoistureGradient: false,
};

// ============================================================================
// Real-Time Data Types (Phase 4)
// ============================================================================

/**
 * Sensor online/offline/stale state tracking
 */
export interface SensorState {
  /** Sensor ID */
  id: string;
  /** Current status */
  status: 'online' | 'offline' | 'stale';
  /** Last time sensor was seen/updated */
  lastSeen: Date;
  /** Last recorded moisture value */
  lastValue: number;
}

/**
 * Real-time data update configuration
 */
export interface RealTimeConfig {
  /** Enable real-time WebSocket updates */
  enabled: boolean;
  /** Auto-refresh interval in seconds (0 = disabled) */
  refreshInterval: number;
  /** Seconds before marking sensor as stale */
  staleThreshold: number;
  /** Seconds before marking sensor as offline */
  offlineThreshold: number;
}

/**
 * Default real-time configuration values
 */
export const DEFAULT_REALTIME_CONFIG: RealTimeConfig = {
  enabled: false,
  refreshInterval: 0,
  staleThreshold: 300, // 5 minutes
  offlineThreshold: 900, // 15 minutes
};

// ============================================================================
// Component Props & Properties
// ============================================================================

/**
 * Props for the MapComponent React component
 */
export interface MapComponentProps {
  /** Component data from editor/viewer */
  component: Component;
  /** Whether rendering in preview mode */
  isPreview?: boolean;
}

/**
 * @deprecated Use MapComponentProps instead
 */
export type SoilMoistureMapProps = MapComponentProps;

/**
 * Properties stored on the component.properties object
 */
export interface MapComponentProperties extends Record<string, unknown> {
  // ---------------------------------------------------------------------------
  // Data Source
  // ---------------------------------------------------------------------------
  /** Data source configuration (query template or static) */
  dataSource?: ChartDataSource;
  /** Static sensor data (if not using query template) */
  data?: SensorDataPoint[];

  // ---------------------------------------------------------------------------
  // View Settings
  // ---------------------------------------------------------------------------
  /** Initial map view configuration */
  initialView?: MapViewSettings;
  /** Minimum zoom level allowed */
  minZoom?: number;
  /** Maximum zoom level allowed */
  maxZoom?: number;

  // ---------------------------------------------------------------------------
  // Layer Configuration
  // ---------------------------------------------------------------------------
  /** Layer visibility and styling configuration */
  layers?: Partial<MapLayerConfig>;
  /** Currently active primary layer */
  activeLayer?: 'heatmap' | 'markers' | 'clusters';

  // ---------------------------------------------------------------------------
  // Display Settings
  // ---------------------------------------------------------------------------
  /** Map title displayed in the legend/header */
  title?: string;
  /** Map description */
  description?: string;
  /** Color theme name */
  colorTheme?: string;
  /** Tile provider for base map */
  tileProvider?: 'maptiler' | 'stadia' | 'osm';
  /** Map style - streets, satellite, or hybrid (satellite with labels) */
  mapStyle?: 'streets' | 'satellite' | 'hybrid';

  // ---------------------------------------------------------------------------
  // Interactivity & Controls (Phase 5)
  // ---------------------------------------------------------------------------
  /** Show navigation controls (zoom, compass) */
  showControls?: boolean;
  /** Navigation controls configuration */
  controlsConfig?: Partial<MapControlsConfig>;
  /** Show color legend */
  showLegend?: boolean;
  /** Legend configuration */
  legendConfig?: Partial<MapLegendConfig>;
  /** Show tooltip on hover */
  showTooltip?: boolean;
  /** Enable sensor clustering */
  enableClustering?: boolean;
  /** Callback when sensor is clicked */
  onSensorClick?: (sensor: SensorDataPoint) => void;

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------
  /** Filter by moisture level range [min, max] */
  moistureRange?: [number, number];
  /** Filter by date range */
  dateRange?: [Date, Date];
  /** Filter to specific zones */
  selectedZones?: string[];

  // ---------------------------------------------------------------------------
  // Groups & Pins
  // ---------------------------------------------------------------------------
  /** Map groups for organizing sensors and pins */
  groups?: MapGroup[];
  /** IDs of currently visible groups (undefined = all visible) */
  visibleGroups?: string[];
  /** @deprecated Use showLayerPanel instead */
  showGroupFilter?: boolean;
  /** Show layer panel UI on map */
  showLayerPanel?: boolean;
  /** Custom map pins */
  pins?: MapPin[];
  /** Pin navigation configuration */
  pinNavigation?: PinNavigationConfig;
  /** Pin layer visibility */
  showPins?: boolean;
  /** Currently selected pin ID (for editing) */
  selectedPinId?: string;
  /** Whether pin placement mode is active */
  pinPlacementMode?: boolean;
  /** Pending pin data (during placement) */
  pendingPin?: Partial<MapPin>;
  /** Pin ID requesting data binding (triggers discovery in AI chat) */
  pinDataBindingRequest?: string;

  // ---------------------------------------------------------------------------
  // Real-Time Settings (Phase 4)
  // ---------------------------------------------------------------------------
  /** Auto-refresh interval in seconds (0 = disabled) */
  refreshInterval?: number;
  /** Enable real-time updates via WebSocket */
  enableRealTime?: boolean;
  /** Seconds before marking sensor as stale (default: 300 = 5 min) */
  staleThreshold?: number;
  /** Seconds before marking sensor as offline (default: 900 = 15 min) */
  offlineThreshold?: number;
  /**
   * WebSocket subscription group ID for real-time data updates.
   * NOTE: This is distinct from the `groups` property which defines
   * MapGroup[] for filtering/organizing sensors and pins on the map.
   */
  groupId?: string;

  // ---------------------------------------------------------------------------
  // Size Constraints
  // ---------------------------------------------------------------------------
  /** Minimum height of the map container */
  minHeight?: string | number;

  // ---------------------------------------------------------------------------
  // Location Lock
  // ---------------------------------------------------------------------------
  /**
   * When true, auto-sync of viewport state to initialView is disabled.
   * Only explicit "set location" commands update initialView.
   * Use "unlock location" to resume auto-sync.
   */
  locationLocked?: boolean;

  // ---------------------------------------------------------------------------
  // Performance Settings (Phase 8)
  // ---------------------------------------------------------------------------
  /** Minimum number of sensors to show at all zoom levels (default: auto-calculated) */
  minVisibleSensors?: number;
  /** Enable performance monitoring in development mode */
  enablePerformanceMonitoring?: boolean;
}

/**
 * @deprecated Use MapComponentProperties instead
 */
export type SoilMoistureMapProperties = MapComponentProperties;

// ============================================================================
// Color Theme Definitions
// ============================================================================

/**
 * Predefined color themes for heatmap visualization
 */
export const MAP_COLOR_THEMES: Record<string, string[]> = {
  agricultural: [
    'rgba(255, 255, 255, 0)',   // transparent
    'rgb(178, 102, 64)',        // brown (dry)
    'rgb(219, 180, 100)',       // tan
    'rgb(161, 217, 155)',       // light green (optimal)
    'rgb(65, 171, 93)',         // green
    'rgb(103, 169, 207)',       // light blue
    'rgb(33, 102, 172)'         // blue (saturated)
  ],
  thermal: [
    'rgba(255, 255, 255, 0)',
    'rgb(255, 245, 240)',       // very light
    'rgb(254, 224, 144)',       // light yellow
    'rgb(254, 178, 76)',        // yellow-orange
    'rgb(252, 78, 42)',         // orange-red
    'rgb(177, 0, 38)'           // red
  ],
  ocean: [
    'rgba(255, 255, 255, 0)',
    'rgb(237, 248, 251)',       // very light blue
    'rgb(179, 205, 227)',       // light blue
    'rgb(107, 174, 214)',       // medium blue
    'rgb(33, 113, 181)',        // blue
    'rgb(8, 48, 107)'           // dark blue
  ],
  grayscale: [
    'rgba(255, 255, 255, 0)',
    'rgb(240, 240, 240)',       // very light gray
    'rgb(189, 189, 189)',       // light gray
    'rgb(115, 115, 115)',       // medium gray
    'rgb(64, 64, 64)',          // dark gray
    'rgb(37, 37, 37)'           // very dark gray
  ]
};

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default map view settings (US center)
 */
export const DEFAULT_MAP_VIEW: MapViewSettings = {
  center: [-98.5, 39.8],
  zoom: 4,
  pitch: 0,
  bearing: 0
};

/**
 * Default layer configuration
 */
export const DEFAULT_LAYER_CONFIG: MapLayerConfig = {
  heatmap: {
    enabled: true,
    colorScheme: 'agricultural',
    radius: 20,
    intensity: 1,
    opacity: 0.8,
  },
  markers: {
    enabled: false,
    showLabels: false,
    size: 'medium',
    colorByValue: true,
  },
  clusters: {
    enabled: false,
    radius: 50,
    maxZoom: 14,
  },
  zones: {
    enabled: false,
    fillOpacity: 0.3,
    strokeWidth: 2,
  },
};

/**
 * Default pin styling
 */
export const DEFAULT_PIN_STYLE: MapPinStyle = {
  color: '#1E3A5F',
  icon: 'location',
  scale: 1.0,
  labelPosition: 'bottom',
};

/**
 * Default pin navigation configuration
 */
export const DEFAULT_PIN_NAVIGATION: PinNavigationConfig = {
  enabled: true,
  defaultTarget: undefined,
};

/**
 * Default color palette for groups
 */
export const DEFAULT_GROUP_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#F97316', // Orange
] as const;

/**
 * Default sensor data - empty by default, requires data source configuration
 */
export const DEFAULT_SENSOR_DATA: SensorDataPoint[] = [];

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Extended ViewState with our custom properties
 */
export interface MapViewState extends ViewState {
  transitionDuration?: number;
}

/**
 * Sensor tooltip data
 */
export interface SensorTooltipData {
  sensor: SensorDataPoint;
  x: number;
  y: number;
}

/**
 * Map event types for callbacks
 */
export type MapEventType = 'click' | 'hover' | 'zoom' | 'pan' | 'load';
